import { Box } from '@mui/material';
import cn from 'classnames';
import React, { forwardRef } from 'react';
import { useHistory } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from '../../../assets/images/icon-delete-button.svg';

import { routes } from '../../../constants/routing';
import { PER_PAGE_OPTIONS } from '../../../constants/table';
import useClearFiltersCallback from '../../../hooks/useClearFiltersCallback';
import { IOneLink, OneLinkStatusEnum } from '../../../types/oneLink';
import { UISizeEnum } from '../../../types/shell';
import { IFiltersRef } from '../../../types/tables';
import { ActionButton, AsyncButton, ReportsCopyableButton } from '../../forms';
import { OneLinkCard } from '../../index';
import PaginatedList, { IPaginatedListRef } from '../../PaginatedList/PaginatedList';
import { PageContent, PaginationControls, TableFilteredEmptyPlaceholder, TableViewLoader } from '../../_mui';

import styles from './OneLinksManagerGridView.module.scss';

interface IProps {
  totalCount: number;
  filteredOneLinks: IOneLink[];
  onChange: (event: { page: number; setPage: (p: number) => void }) => void;
  isFinalOneLink: boolean;
  isOneLinksFetching: boolean;
  mobile?: boolean;
  onOpenDeactivateDialog: (item: IOneLink) => void;
  disabledLoaderIds?: string[];
  filtersRef: React.RefObject<IFiltersRef>;
  page?: number;
  pageSize?: number;
  onPageSizeChange?: (newPageSize: number) => void;
}

const OneLinkManagerGridView = forwardRef<IPaginatedListRef<IOneLink>, IProps>(
  (
    {
      totalCount,
      filteredOneLinks,
      onChange,
      isFinalOneLink,
      isOneLinksFetching,
      mobile,
      onOpenDeactivateDialog,
      disabledLoaderIds,
      filtersRef,
      page,
      pageSize = 10,
      onPageSizeChange,
    },
    paginatedListRef,
  ) => {
    const history = useHistory();
    const handleClearFilters = useClearFiltersCallback(filtersRef);

    const renderContent = (items: IOneLink[]) => {
      if (!filteredOneLinks || !filteredOneLinks.length) {
        return <TableFilteredEmptyPlaceholder itemType="one links" onClearFilters={handleClearFilters} />;
      }

      return (
        <PageContent>
          <Box
            sx={{
              width: {
                xs: '100%',
                lg: '70%',
              },
              maxWidth: '1000px',
              maxHeight: '100%',
              overflowY: 'auto',
            }}
          >
            {items.map((oneLink: IOneLink) => {
              const isActive = oneLink.status === OneLinkStatusEnum.Active;
              const disableInProgress = disabledLoaderIds?.includes(oneLink.uid);
              return (
                <OneLinkCard
                  key={oneLink.uid}
                  engagement={oneLink}
                  wide
                  className={styles.card}
                  controls={[
                    <ReportsCopyableButton
                      key="copy-link"
                      hint="Copy link"
                      className={cn(styles.control, { [styles.mobile]: mobile })}
                      isIcon={mobile}
                      value={oneLink.url}
                      toastLabel="Claim URL"
                    />,
                    oneLink?.password && (
                      <ReportsCopyableButton
                        key="copy-password"
                        hint="Copy password"
                        className={cn(styles.control, { [styles.mobile]: mobile })}
                        isIcon={mobile}
                        value={oneLink.password}
                        name="Copy password"
                        toastLabel="Password"
                      />
                    ),
                    isActive && (
                      <AsyncButton
                        isLoading={disableInProgress}
                        className={cn(styles.control, styles.disable, {
                          [styles.mobile]: mobile,
                          [styles.isLoading]: disableInProgress,
                        })}
                        key="stop-button"
                        onClick={() => onOpenDeactivateDialog(oneLink)}
                        loaderClassName={styles.disableLoader}
                      >
                        {mobile ? <DeleteIcon /> : 'Stop campaign'}
                      </AsyncButton>
                    ),
                    <ActionButton
                      size={UISizeEnum.Small}
                      outlined
                      className={cn(styles.control, styles.viewButton, { [styles.mobile]: mobile })}
                      key="view-button"
                      title="View summary"
                      onClick={() =>
                        history.push(
                          routes.oneLinkManager.getOneLinkManagerUrl({
                            itemId: oneLink.uid,
                            status: oneLink.status as OneLinkStatusEnum,
                          }),
                        )
                      }
                    />,
                  ]}
                />
              );
            })}
          </Box>
        </PageContent>
      );
    };

    return (
      <Box className={styles.gridViewContainer}>
        <PaginatedList<IOneLink>
          ref={paginatedListRef}
          items={filteredOneLinks}
          pageSize={pageSize}
          totalCount={totalCount}
          onChange={onChange}
          isFinalItems={isFinalOneLink}
          defaultValue={page}
        >
          {({ items: oneLinks, pagination: PaginationComponent }) => {
            const visibleOneLinks = !isOneLinksFetching && oneLinks.length > 0;

            return (
              <>
                {isOneLinksFetching ? <TableViewLoader /> : renderContent(oneLinks)}
                {visibleOneLinks && (
                  <PaginationControls
                    pageSize={pageSize}
                    options={PER_PAGE_OPTIONS}
                    onPageSizeChange={onPageSizeChange!}
                    PaginationComponent={PaginationComponent}
                  />
                )}
              </>
            );
          }}
        </PaginatedList>
      </Box>
    );
  },
);

export default OneLinkManagerGridView;
